// components/atoms/ConfirmDialog.tsx
import { Button } from "@/components/atoms/Button";
import * as Dialog from "@radix-ui/react-dialog";
import { Trash2, X } from "lucide-react";
import { ConfirmDialogProps } from "./ConfirmDialog.types";
import { cn } from "@/utils/cn";
import {
  InfoIcon,
  AlertTriangleIcon,
  XCircleIcon,
  CheckCircleIcon,
} from "lucide-react";

const variantIcons = {
  info: <InfoIcon className="w-16 h-16 text-blue-500" />,
  warning: <AlertTriangleIcon className="w-16 h-16 text-yellow-500" />,
  danger: <XCircleIcon className="w-16 h-16 text-red-500" />,
  success: <CheckCircleIcon className="w-16 h-16 text-green-500" />,
  delete: <Trash2 className="w-16 h-16 text-red-500" />,
};

export const ConfirmDialog = ({
  open,
  title,
  description,
  onCancel,
  onConfirm,
  variant = "info",
  iconOverride,
}: ConfirmDialogProps) => {
  const icon = iconOverride ?? variantIcons[variant];

  return (
    <Dialog.Root open={open} onOpenChange={(open) => !open && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50" />
        <Dialog.Content
          className={cn(
            "fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
            "bg-white rounded-md p-6 shadow-lg w-full max-w-md",
            "dark:border-[--color-neutral-dark] dark:bg-[--secondary-900] dark:text-white",
            "border shadow-neumorph"
          )}
        >
          <div className="flex justify-end items-start">
            <Dialog.Close asChild>
              <Button size="xs" onClick={onCancel} variant="link">
                <X className="h-5 w-5 text-gray-500" />
              </Button>
            </Dialog.Close>
          </div>

          <div className="flex flex-col justify-center items-center gap-2">
            {icon && <span>{icon}</span>}
            <Dialog.Title className="text-lg font-semibold">{title}</Dialog.Title>
          </div>
          {description && (
            <Dialog.Description asChild={typeof description !== "string"} className="flex justify-center mt-2 text-sm text-gray-600 dark:text-gray-300">
              {description}
            </Dialog.Description>
          )}

          <div className="mt-6 flex justify-center gap-2">
            <Button variant="secondary" onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
            >
              Confirmar
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
